import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Star, Upload, AlertCircle, CheckCircle, Camera } from 'lucide-react';
import { ratingService } from '../../services/ratingService';
import { productService } from '../../services/productService';
import { componentService } from '../../services/componentService';
import { useAuth } from '../../contexts/AuthContext';
import { imageUploadService } from '../../utils/imageUploadService';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';

const ReviewSubmissionForm = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const productId = searchParams?.get('productId');
  const componentId = searchParams?.get('componentId');
  
  const [product, setProduct] = useState(null);
  const [component, setComponent] = useState(null);
  const [rating, setRating] = useState(0);
  const [review, setReview] = useState('');
  const [hoveredStar, setHoveredStar] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [existingReview, setExistingReview] = useState(null);
  const [charCount, setCharCount] = useState(0);

  const minChars = 50;
  const maxChars = 2000;

  useEffect(() => {
    if (!user) {
      navigate('/signin');
      return;
    }

    if (productId || componentId) {
      loadDataAndReview();
    }
  }, [productId, componentId, user]);

  const loadDataAndReview = async () => {
    try {
      setLoading(true);
      setError(null);

      if (componentId) {
        const compData = await componentService?.getById(componentId);
        setComponent(compData);
        
        if (user && compData?.globalComponentId) {
          const existingData = await ratingService?.getUserComponentRating(
            compData.globalComponentId,
            user?.id
          );
          if (existingData) {
            setExistingReview(existingData);
            setRating(existingData?.rating);
            setReview(existingData?.review || '');
            setCharCount((existingData?.review || '')?.length);
          }
        }
      } else if (productId) {
        const productData = await productService?.getById(parseInt(productId));
        setProduct(productData);

        if (user) {
          const existingData = await ratingService?.getUserProductRating(
            parseInt(productId),
            user?.id
          );
          
          if (existingData) {
            setExistingReview(existingData);
            setRating(existingData?.rating);
            setReview(existingData?.review || '');
            setCharCount((existingData?.review || '')?.length);
          }
        }
      }
    } catch (err) {
      console.error('Error loading data:', err);
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleReviewChange = (e) => {
    const text = e?.target?.value;
    if (text?.length <= maxChars) {
      setReview(text);
      setCharCount(text?.length);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!rating) {
      setError('Please select a star rating');
      return;
    }

    if (review?.length < minChars) {
      setError(`Review must be at least ${minChars} characters`);
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      if (componentId) {
        if (existingReview) {
          await ratingService?.updateComponentRating(
            existingReview?.id,
            rating,
            review
          );
        } else {
          await ratingService?.createComponentRating(
            componentId,
            rating,
            review
          );
        }
      } else if (productId) {
        if (existingReview) {
          await ratingService?.updateProductRating(
            existingReview?.id,
            rating,
            review
          );
        } else {
          await ratingService?.createProductRating(
            parseInt(productId),
            rating,
            review
          );
        }
      }

      setSuccess(true);
      
      setTimeout(() => {
        if (componentId) {
           navigate(`/product-reviews-ratings?componentId=${componentId}`);
        } else {
           navigate(`/product-reviews-ratings?productId=${productId}`);
        }
      }, 2000);
    } catch (err) {
      console.error('Error submitting review:', err);
      setError(err?.message);
    } finally {
      setSubmitting(false);
    }
  };

  const getRatingDescription = (stars) => {
    const descriptions = {
      1: 'Poor - Significantly below expectations',
      2: 'Fair - Below expectations',
      3: 'Good - Meets expectations',
      4: 'Very Good - Exceeds expectations',
      5: 'Excellent - Far exceeds expectations'
    };
    return descriptions?.[stars] || 'Select a rating';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  const targetItem = componentId ? component : product;
  const targetName = componentId ? `${targetItem?.customName || targetItem?.name} (for ${targetItem?.product?.name || 'Unknown Product'})` : targetItem?.name;
  let targetImage = '/api/placeholder/80/80';
  if (componentId) {
    if (targetItem?.images?.[0]) {
      targetImage = targetItem.images[0].startsWith('http') ? targetItem.images[0] : imageUploadService.getPublicUrl(targetItem.images[0]);
    } else if (targetItem?.product?.thumbnailUrl) {
      targetImage = targetItem.product.thumbnailUrl;
    }
  } else {
    targetImage = targetItem?.thumbnailPath || '/api/placeholder/80/80';
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {success && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-green-800">Review Submitted Successfully!</h3>
              <p className="text-green-700 text-sm">Redirecting to reviews page...</p>
            </div>
          </div>
        )}

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-red-800">Error</h3>
              <p className="text-red-600 text-sm mt-1">{error}</p>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow-sm p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {existingReview ? 'Update Your Review' : 'Write a Review'}
          </h1>
          <p className="text-gray-600 mb-8">
            Share your experience with this {componentId ? 'component' : 'product'} to help other customers make informed decisions.
          </p>

          {/* Product/Component Info */}
          {targetItem && (
            <div className="bg-gray-50 rounded-lg p-4 mb-8 flex items-center gap-4">
              <img
                src={targetImage}
                alt={targetName}
                className="w-20 h-20 object-cover rounded-lg border border-gray-200"
              />
              <div>
                <h3 className="font-semibold text-gray-900">{targetName}</h3>
                <p className="text-sm text-gray-600">${targetItem?.price}</p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Rating Selection */}
            <div>
              <label className="block text-lg font-semibold text-gray-900 mb-3">
                Your Rating <span className="text-red-500">*</span>
              </label>
              <div className="flex items-center gap-4 mb-2">
                <div className="flex items-center gap-2">
                  {[1, 2, 3, 4, 5]?.map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setRating(star)}
                      onMouseEnter={() => setHoveredStar(star)}
                      onMouseLeave={() => setHoveredStar(0)}
                      className="transition-transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded"
                    >
                      <Star
                        className={`w-10 h-10 transition-colors ${
                          star <= (hoveredStar || rating)
                            ? 'fill-yellow-400 text-yellow-400' :'text-gray-300 hover:text-yellow-200'
                        }`}
                      />
                    </button>
                  ))}
                </div>
                {rating > 0 && (
                  <span className="text-lg font-medium text-gray-700">
                    {rating} {rating === 1 ? 'Star' : 'Stars'}
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-600 italic">
                {getRatingDescription(hoveredStar || rating)}
              </p>
            </div>

            {/* Review Guidelines */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-900 mb-2">Review Guidelines</h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Be specific about what you liked or didn't like</li>
                <li>• Mention product quality, features, and performance</li>
                <li>• Include details about how you're using the product</li>
                <li>• Keep it respectful and constructive</li>
                <li>• Avoid mentioning competitors or pricing</li>
              </ul>
            </div>

            {/* Review Text */}
            <div>
              <label className="block text-lg font-semibold text-gray-900 mb-3">
                Your Review <span className="text-red-500">*</span>
              </label>
              <textarea
                value={review}
                onChange={handleReviewChange}
                rows={8}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                placeholder="Share your detailed experience with this product. What did you love? What could be improved? How are you using it?"
              />
              <div className="flex items-center justify-between mt-2">
                <p className="text-sm text-gray-600">
                  {charCount < minChars ? (
                    <span className="text-orange-600">
                      {minChars - charCount} more characters needed
                    </span>
                  ) : (
                    <span className="text-green-600">✓ Meets minimum length</span>
                  )}
                </p>
                <p className="text-sm text-gray-600">
                  {charCount} / {maxChars}
                </p>
              </div>
            </div>

            {/* Photo Upload Placeholder */}
            <div>
              <label className="block text-lg font-semibold text-gray-900 mb-3">
                Add Photos (Optional)
              </label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-500 transition-colors cursor-pointer">
                <Camera className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-600 mb-1">Click to upload product photos</p>
                <p className="text-sm text-gray-500">
                  Coming soon: Add up to 5 photos showing the product in use
                </p>
              </div>
            </div>

            {/* Submit Buttons */}
            <div className="flex items-center gap-4 pt-6 border-t border-gray-200">
              <Button
                type="submit"
                disabled={submitting || !rating || charCount < minChars}
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 text-lg font-semibold disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Submitting...
                  </>
                ) : existingReview ? (
                  'Update Review'
                ) : (
                  'Submit Review'
                )}
              </Button>
              <Button
                type="button"
                onClick={() => navigate(-1)}
                className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-8 py-3"
              >
                Cancel
              </Button>
            </div>

            {/* Preview Mode */}
            {rating > 0 && charCount >= minChars && (
              <div className="mt-8 pt-8 border-t border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Preview Your Review</h3>
                <div className="bg-gray-50 rounded-lg p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center text-white font-semibold">
                      {user?.user_metadata?.first_name?.[0]}{user?.user_metadata?.last_name?.[0]}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">
                        {user?.user_metadata?.first_name} {user?.user_metadata?.last_name}
                      </p>
                      <div className="flex items-center">
                        {[...Array(5)]?.map((_, i) => (
                          <Star
                            key={i}
                            className={`w-4 h-4 ${
                              i < rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'
                            }`}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                  <p className="text-gray-700 whitespace-pre-wrap">{review}</p>
                </div>
              </div>
            )}
          </form>
        </div>

        {/* Help Section */}
        <div className="mt-8 bg-white rounded-lg shadow-sm p-6">
          <h3 className="font-semibold text-gray-900 mb-3">Need Help?</h3>
          <p className="text-gray-600 text-sm">
            Having issues with the product? Contact our support team before leaving a review. 
            We're here to help resolve any problems you may encounter.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ReviewSubmissionForm;